import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, AlertTriangle, Wrench, ClipboardList as ClipIcon, Search, RefreshCw, CheckCircle2, Download } from "lucide-react";

const INTERVENTION_TYPE_LABELS = {
  prima: { label: "Prima installazione", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  manutenzione: { label: "Manutenzione", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  predisposizione: { label: "Predisposizione", cls: "bg-sky-100 text-sky-800 border-sky-200" },
};

export default function LavorazioniPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isTecnico = user?.role === "tecnico";
  const [technicians, setTechnicians] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [listini, setListini] = useState([]);
  const [lavorazioni, setLavorazioni] = useState([]);
  const [voceQuery, setVoceQuery] = useState("");
  const [filterTech, setFilterTech] = useState("all");
  const [filterCommittente, setFilterCommittente] = useState("all");
  // Default filtro data: mese corrente (per non appesantire il caricamento)
  const _pad2 = (n) => String(n).padStart(2, "0");
  const _today = new Date();
  const _firstOfMonth = `${_today.getFullYear()}-${_pad2(_today.getMonth() + 1)}-01`;
  const _lastOfMonth = (() => {
    const d = new Date(_today.getFullYear(), _today.getMonth() + 1, 0);
    return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
  })();
  const [filterDateFrom, setFilterDateFrom] = useState(_firstOfMonth);
  const [filterDateTo, setFilterDateTo] = useState(_lastOfMonth);
  const clearDateFilter = () => { setFilterDateFrom(""); setFilterDateTo(""); };
  const setCurrentMonth = () => { setFilterDateFrom(_firstOfMonth); setFilterDateTo(_lastOfMonth); };
  const setPreviousMonth = () => {
    const d = new Date(_today.getFullYear(), _today.getMonth() - 1, 1);
    const first = `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-01`;
    const lastD = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const last = `${lastD.getFullYear()}-${_pad2(lastD.getMonth() + 1)}-${_pad2(lastD.getDate())}`;
    setFilterDateFrom(first); setFilterDateTo(last);
  };
  const isCurrentMonthActive = filterDateFrom === _firstOfMonth && filterDateTo === _lastOfMonth;
  const isDateFilterEmpty = !filterDateFrom && !filterDateTo;
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    technician_id: "", co_technician_ids: [], committente_id: "", listino_id: "",
    date: new Date().toISOString().slice(0, 10),
    intervention_ref: "", intervention_type: null, address: "", righe: [], notes: "",
  });

  const [dupCheck, setDupCheck] = useState({ loading: false, duplicates: [], ref: "" });

  const load = async () => {
    const params = {};
    if (filterTech && filterTech !== "all") params.technician_id = filterTech;
    if (filterCommittente && filterCommittente !== "all") params.committente_id = filterCommittente;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    const { data } = await api.get("/lavorazioni", { params });
    setLavorazioni(data);
  };
  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechnicians(r.data));
    api.get("/committenti").then((r) => setCommittenti(r.data));
    api.get("/listini").then((r) => setListini(r.data));
  }, []);
  useEffect(() => { load(); }, [filterTech, filterCommittente, filterDateFrom, filterDateTo]);

  // Lavorazioni "attive" (escluse le pre_chiusura non ancora approvate) usate per i KPI.
  // Le pre_chiusura restano visibili in tabella con il loro badge ma NON contano
  // nel totale produzione né nel conteggio: allineato al backend che le esclude ovunque
  // (Dashboard, BEF, Produzione Obiettivo, Produzione per Committente).
  const lavorazioniAttive = useMemo(
    () => lavorazioni.filter((l) => l.status !== "pre_chiusura"),
    [lavorazioni]
  );
  const totaleProduzione = useMemo(() => {
    // Se filtriamo per un tecnico specifico, sommiamo la QUOTA pro-tecnico
    // (l.total / (1 + co_technician_ids.length)) invece del totale intero.
    // Così se Mario e Pino fanno insieme 300 €, filtrando per Mario vedo 150 (la sua quota).
    if (filterTech && filterTech !== "all") {
      return lavorazioniAttive.reduce((sum, l) => {
        const shareCount = 1 + (l.co_technician_ids || []).length;
        return sum + (Number(l.total) || 0) / shareCount;
      }, 0);
    }
    return lavorazioniAttive.reduce((sum, l) => sum + (Number(l.total) || 0), 0);
  }, [lavorazioniAttive, filterTech]);
  const pendingPreChiusuraCount = useMemo(
    () => lavorazioni.filter((l) => l.status === "pre_chiusura").length,
    [lavorazioni]
  );

  const currentListino = useMemo(() => listini.find((l) => l.id === form.listino_id), [listini, form.listino_id]);

  const filteredVoci = useMemo(() => {
    const q = (voceQuery || "").trim().toLowerCase();
    const all = (currentListino?.voci || []);
    if (!q) return all;
    return all.filter((v) =>
      (v.code || "").toLowerCase().includes(q) ||
      (v.description || "").toLowerCase().includes(q)
    );
  }, [voceQuery, currentListino]);
  const listiniOfCommittente = useMemo(() => listini.filter((l) => l.committente_id === form.committente_id), [listini, form.committente_id]);

  const openNew = () => {
    setEditingId(null);
    setForm({
      technician_id: "", co_technician_ids: [], committente_id: "", listino_id: "",
      date: new Date().toISOString().slice(0, 10),
      intervention_ref: "", intervention_type: null, address: "", righe: [], notes: "",
    });
    setDupCheck({ loading: false, duplicates: [], ref: "" });
    setVoceQuery("");
    setOpen(true);
  };

  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      technician_id: l.technician_id || "",
      co_technician_ids: l.co_technician_ids || [],
      committente_id: l.committente_id || "",
      listino_id: l.listino_id || "",
      date: l.date || new Date().toISOString().slice(0, 10),
      intervention_ref: l.intervention_ref || "",
      intervention_type: l.intervention_type || null,
      address: l.address || "",
      righe: l.righe || [],
      notes: l.notes || "",
    });
    setVoceQuery("");
    setOpen(true);
  };

  const toggleCoTech = (tid) => {
    setForm((f) => {
      const arr = f.co_technician_ids || [];
      return { ...f, co_technician_ids: arr.includes(tid) ? arr.filter((x) => x !== tid) : [...arr, tid] };
    });
  };

  // Debounced duplicate check when intervention_ref changes
  useEffect(() => {
    const ref = (form.intervention_ref || "").trim();
    if (!open || !ref) {
      setDupCheck({ loading: false, duplicates: [], ref: "" });
      return;
    }
    setDupCheck((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/lavorazioni/check-duplicate", { params: { intervention_ref: ref, exclude_id: editingId || undefined } });
        setDupCheck({ loading: false, duplicates: data.duplicates || [], ref: data.intervention_ref });
        // auto-clear the chosen type if the ref changed
        if ((data.duplicates || []).length === 0) {
          setForm((f) => (f.intervention_type ? { ...f, intervention_type: null } : f));
        }
      } catch {
        setDupCheck({ loading: false, duplicates: [], ref });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.intervention_ref, open, editingId]);

  const hasDuplicates = dupCheck.duplicates.length > 0;

  const addRiga = (voce) => {
    setForm((f) => ({
      ...f,
      righe: [...f.righe, { code: voce.code, description: voce.description, quantity: 1, unit_price: voce.price }],
    }));
  };
  const updRiga = (i, k, v) => {
    const righe = [...form.righe];
    righe[i] = { ...righe[i], [k]: k === "quantity" || k === "unit_price" ? Number(v) || 0 : v };
    setForm({ ...form, righe });
  };
  const rmRiga = (i) => setForm({ ...form, righe: form.righe.filter((_, idx) => idx !== i) });

  // Aggiunge una riga "una tantum" (fuori listino) — l'utente compila code/descrizione/prezzo manualmente
  const addRigaCustom = () => {
    setForm((f) => ({
      ...f,
      righe: [...f.righe, { code: "EXTRA", description: "", quantity: 1, unit_price: 0 }],
    }));
  };

  const total = form.righe.reduce((s, r) => s + r.quantity * r.unit_price, 0);

  const save = async () => {
    if (!form.technician_id || !form.committente_id || form.righe.length === 0)
      return toast.error("Compila tecnico, committente e almeno una voce (il listino è opzionale se aggiungi voci una tantum)");
    if (hasDuplicates && !form.intervention_type) {
      return toast.error(t("auto.il_numero_impianto_è_già_presente_scegli_il_tipo_di_interven"));
    }
    try {
      const payload = { ...form, intervention_type: hasDuplicates ? form.intervention_type : (form.intervention_type || null) };
      if (editingId) {
        await api.patch(`/lavorazioni/${editingId}`, payload);
        toast.success("Lavorazione aggiornata");
      } else {
        await api.post("/lavorazioni", payload);
        toast.success("Lavorazione salvata");
      }
      setOpen(false);
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/lavorazioni/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Adegua listino: dialog + logica
  const [adeguaOpen, setAdeguaOpen] = useState(false);
  const [adeguaForm, setAdeguaForm] = useState({
    date_from: "",
    date_to: "",
    committente_id: "all",
    technician_id: "all",
  });
  const [adeguaLoading, setAdeguaLoading] = useState(false);
  const [adeguaResult, setAdeguaResult] = useState(null);

  const openAdegua = () => {
    // Default: mese corrente
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setAdeguaForm({
      date_from: firstDay,
      date_to: lastDay,
      committente_id: filterCommittente !== "all" ? filterCommittente : "all",
      technician_id: filterTech !== "all" ? filterTech : "all",
    });
    setAdeguaResult(null);
    setAdeguaOpen(true);
  };

  const runAdegua = async (dryRun) => {
    if (!adeguaForm.date_from || !adeguaForm.date_to) {
      return toast.error("Seleziona le date da e a");
    }
    setAdeguaLoading(true);
    try {
      const payload = {
        date_from: adeguaForm.date_from,
        date_to: adeguaForm.date_to,
        dry_run: dryRun,
      };
      if (adeguaForm.committente_id !== "all") payload.committente_id = adeguaForm.committente_id;
      if (adeguaForm.technician_id !== "all") payload.technician_id = adeguaForm.technician_id;
      const { data } = await api.post("/lavorazioni/adegua-listino", payload);
      setAdeguaResult(data);
      if (!dryRun) {
        toast.success(`Adeguamento completato: ${data.lavorazioni_affected} lavorazioni aggiornate (Δ € ${data.delta_sum.toFixed(2)})`);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante l'adeguamento");
    } finally {
      setAdeguaLoading(false);
    }
  };


  const techName = (id) => technicians.find((tech) => tech.id === id)?.name || "—";
  const commName = (id) => committenti.find((c) => c.id === id)?.name || "—";

  // Esporta le lavorazioni filtrate in Excel
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterTech && filterTech !== "all") params.technician_id = filterTech;
      if (filterCommittente && filterCommittente !== "all") params.committente_id = filterCommittente;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await api.get("/lavorazioni/export-excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const commSlug = filterCommittente !== "all" ? `_${commName(filterCommittente).replace(/\s+/g, "-").toLowerCase()}` : "";
      const dateSlug = filterDateFrom || filterDateTo ? `_${filterDateFrom || ""}_${filterDateTo || ""}` : "";
      a.download = `lavorazioni${commSlug}${dateSlug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export Excel scaricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore export Excel");
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Produzione</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.works")}</h1>
        </div>
        {isAdmin && (
          <div className="flex items-end gap-2">
            <Button
              onClick={openAdegua}
              variant="outline"
              className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="btn-adegua-listino"
              title="Riallinea gli unit_price delle lavorazioni al listino attuale"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Adegua listino
            </Button>
            <Button onClick={openNew} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-lavorazione"><Plus className="mr-2 h-4 w-4" /> Nuova lavorazione</Button>
          </div>
        )}
      </div>

      <div className={`flex flex-wrap items-end gap-3 ${isTecnico ? "hidden" : ""}`}>
        <div>
          <Label className="text-xs text-slate-500">{t("auto.filtra_tecnico")}</Label>
          <Select value={filterTech} onValueChange={setFilterTech}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="filter-tech"><SelectValue placeholder={t("auto.tutti")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("auto.tutti")}</SelectItem>
              {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Committente</Label>
          <Select value={filterCommittente} onValueChange={setFilterCommittente}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="filter-committente"><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Data da</Label>
          <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="w-40 rounded-sm" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Data a</Label>
          <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="w-40 rounded-sm" data-testid="filter-date-to" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">Shortcut periodo</Label>
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={setCurrentMonth}
              className={`rounded-sm border px-2 py-1 text-xs font-medium ${isCurrentMonthActive ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="filter-current-month"
            >
              Mese corrente
            </button>
            <button
              type="button"
              onClick={setPreviousMonth}
              className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              data-testid="filter-previous-month"
            >
              Mese scorso
            </button>
            <button
              type="button"
              onClick={clearDateFilter}
              className={`rounded-sm border px-2 py-1 text-xs font-medium ${isDateFilterEmpty ? "border-amber-500 bg-amber-500 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="filter-all-time"
              title="Mostra tutte le lavorazioni (attenzione: caricamento più lento)"
            >
              Tutte
            </button>
          </div>
        </div>
        {(filterTech !== "all" || filterCommittente !== "all" || !isCurrentMonthActive) && (
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => { setFilterTech("all"); setFilterCommittente("all"); setCurrentMonth(); }}
            data-testid="btn-clear-filters"
          >
            Reset filtri
          </Button>
        )}
        <Button
          variant="outline"
          className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          onClick={exportExcel}
          disabled={exporting || lavorazioni.length === 0}
          data-testid="btn-export-excel"
          title="Esporta le lavorazioni filtrate in Excel"
        >
          <Download className="mr-2 h-4 w-4" /> {exporting ? "Preparo…" : "Esporta Excel"}
        </Button>
        <div className="ml-auto flex items-center gap-4 rounded-sm border border-slate-200 bg-white px-4 py-2 text-sm">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Lavorazioni</div>
            <div className="text-xl font-bold text-slate-900" data-testid="total-count">{lavorazioniAttive.length}</div>
            {pendingPreChiusuraCount > 0 && (
              <div className="text-[10px] font-medium text-amber-600" title="Le pre-chiusure non contano nei KPI finché non le approvi" data-testid="pending-pre-chiusura">
                +{pendingPreChiusuraCount} pre-chiusura in attesa
              </div>
            )}
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {filterTech !== "all" ? "Produzione (quota)" : "Produzione"}
            </div>
            <div className="text-xl font-bold text-[#0052FF]" data-testid="total-produzione">€ {totaleProduzione.toFixed(2)}</div>
            {filterTech !== "all" && (
              <div className="text-[10px] text-slate-500">split ÷ co-tecnici applicato</div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">{t("common.date")}</th><th className="p-3">{t("common.technician")}</th><th className="p-3">{t("auto.committente_x")}</th>
              <th className="p-3">{t("auto.impianto")}</th><th className="p-3">{t("common.type")}</th><th className="p-3">{t("auto.righe")}</th><th className="p-3 text-right">{t("common.total")}</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {lavorazioni.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">{t("auto.nessuna_lavorazione")}</td></tr>}
            {lavorazioni.map((l) => {
              const t = l.intervention_type && INTERVENTION_TYPE_LABELS[l.intervention_type];
              return (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{l.date}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <span>{techName(l.technician_id)}</span>
                      {(l.co_technician_ids || []).length > 0 && (
                        <>
                          {(l.co_technician_ids || []).map((cid) => (
                            <span key={cid} className="rounded-sm border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800" data-testid={`lav-co-${l.id}-${cid}`}>
                              + {techName(cid)}
                            </span>
                          ))}
                          <span className="rounded-sm bg-blue-600 px-1 py-0.5 text-[10px] font-bold text-white" title={`Diviso in ${1 + (l.co_technician_ids || []).length}`}>
                            ÷{1 + (l.co_technician_ids || []).length}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="p-3">{commName(l.committente_id)}</td>
                  <td className="p-3 font-mono text-xs">
                    {l.intervention_ref || "—"}
                    {l.status === "pre_chiusura" && (
                      <div className="mt-1 inline-block rounded-sm bg-amber-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" data-testid={`lav-badge-prechiusura-${l.id}`}>
                        PRE-CHIUSURA
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {t ? (
                      <span className={`inline-block rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${t.cls}`} data-testid={`lav-type-${l.id}`}>{t.label}</span>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-xs">{(l.righe || []).map((r) => `${r.quantity}× ${r.code}`).join(", ")}</td>
                  <td className="p-3 text-right font-mono font-semibold">
                    {isTecnico ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <>
                        € {Number(l.total).toFixed(2)}
                        {(l.co_technician_ids || []).length > 0 && (
                          <div className="mt-0.5 text-[10px] font-normal text-emerald-700" data-testid={`lav-share-${l.id}`}>
                            quota €{(Number(l.total) / (1 + (l.co_technician_ids || []).length)).toFixed(2)}/tec
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && l.status === "pre_chiusura" && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await api.post(`/lavorazioni/${l.id}/approve`);
                              toast.success("Lavorazione approvata");
                              load();
                            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
                          }}
                          className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
                          data-testid={`btn-approve-${l.id}`}
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approva
                        </Button>
                      )}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`btn-edit-lavor-${l.id}`}><Wrench className="h-4 w-4 text-slate-600" /></Button>}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => del(l.id)} data-testid={`btn-del-lavor-${l.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0"><DialogTitle>{editingId ? "Modifica lavorazione" : t("auto.nuova_lavorazione")}</DialogTitle></DialogHeader>
          <div className="flex-1 space-y-3 overflow-y-auto pr-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{t("auto.tecnico_principale")}</Label>
              <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v, co_technician_ids: (form.co_technician_ids || []).filter((x) => x !== v) })}>
                <SelectTrigger className="rounded-sm" data-testid="lav-tech"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("auto.committente_x")}</Label>
              <Select value={form.committente_id} onValueChange={(v) => setForm({ ...form, committente_id: v, listino_id: "" })}>
                <SelectTrigger className="rounded-sm" data-testid="lav-comm"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Listino <span className="text-slate-400">(opzionale)</span></Label>
              <Select value={form.listino_id} onValueChange={(v) => setForm({ ...form, listino_id: v })} disabled={!form.committente_id}>
                <SelectTrigger className="rounded-sm" data-testid="lav-listino"><SelectValue placeholder="Scegli (o lascia vuoto)" /></SelectTrigger>
                <SelectContent>{listiniOfCommittente.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("common.date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" /></div>
            <div><Label>{t("auto.rif_impianto")}</Label><Input value={form.intervention_ref} onChange={(e) => setForm({ ...form, intervention_ref: e.target.value })} className="rounded-sm font-mono" data-testid="lav-ref" /></div>
            <div><Label>{t("common.address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" /></div>
          </div>

          {form.technician_id && (
            <div className="rounded-sm border border-blue-200 bg-blue-50/50 p-3" data-testid="co-tech-section">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">{t("auto.cotecnici_dividi_limporto")}</Label>
                {(form.co_technician_ids || []).length > 0 && (
                  <span className="rounded-sm bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    Split in {(form.co_technician_ids || []).length + 1} parti uguali
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {technicians.filter((tech) => tech.id !== form.technician_id).map((tech) => {
                  const active = (form.co_technician_ids || []).includes(tech.id);
                  return (
                    <button
                      key={tech.id}
                      type="button"
                      onClick={() => toggleCoTech(tech.id)}
                      data-testid={`co-tech-${tech.id}`}
                      className={`rounded-sm border-2 px-3 py-1 text-xs font-semibold transition ${
                        active ? "border-blue-600 bg-blue-600 text-white" : "border-blue-200 bg-white text-blue-800 hover:border-blue-400"
                      }`}
                    >
                      {active ? "✓ " : ""}{tech.name}
                    </button>
                  );
                })}
                {technicians.filter((tech) => tech.id !== form.technician_id).length === 0 && (
                  <span className="text-xs text-slate-400 italic">{t("auto.nessun_altro_tecnico_disponibile")}</span>
                )}
              </div>
              <div className="mt-1.5 text-[11px] text-blue-800">
                💡 Se selezioni altri tecnici, il totale della lavorazione viene diviso in parti uguali tra tutti (principale + co-tecnici) nei rispettivi BEF.
              </div>
            </div>
          )}

          {hasDuplicates && (
            <div className="mt-3 rounded-sm border-2 border-amber-400 bg-amber-50 p-4" data-testid="duplicate-warning">
              <div className="mb-2 flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-amber-900">
                    Attenzione: numero impianto <span className="font-mono">{dupCheck.ref}</span> già presente ({dupCheck.duplicates.length} lavorazion{dupCheck.duplicates.length === 1 ? "e" : "i"} pregresse)
                  </div>
                  <div className="mt-1 text-xs text-amber-800">
                    Verifica se si tratta di manutenzione, predisposizione, o se lo stai segnando due volte per errore.
                  </div>
                </div>
              </div>

              <div className="mb-3 max-h-40 space-y-1.5 overflow-y-auto rounded-sm border border-amber-200 bg-white p-2">
                {dupCheck.duplicates.map((d) => {
                  const t = d.intervention_type && INTERVENTION_TYPE_LABELS[d.intervention_type];
                  return (
                    <div key={d.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-1 text-xs last:border-b-0" data-testid={`dup-row-${d.id}`}>
                      <span className="font-mono text-slate-500">{d.date}</span>
                      <span className="font-medium text-slate-800">{d.technician_name}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-600">{d.committente_name}</span>
                      {t && <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${t.cls}`}>{t.label}</span>}
                      <span className="ml-auto font-mono font-semibold text-slate-700">€ {d.total.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="text-xs font-semibold uppercase tracking-wider text-amber-900">{t("auto.tipo_di_questo_intervento")}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, intervention_type: "manutenzione" })}
                  className={`flex items-center gap-1.5 rounded-sm border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${form.intervention_type === "manutenzione" ? "border-amber-600 bg-amber-600 text-white" : "border-amber-300 bg-white text-amber-800 hover:border-amber-500"}`}
                  data-testid="dup-type-manutenzione"
                >
                  <Wrench className="h-3.5 w-3.5" /> Manutenzione
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, intervention_type: "predisposizione" })}
                  className={`flex items-center gap-1.5 rounded-sm border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${form.intervention_type === "predisposizione" ? "border-sky-600 bg-sky-600 text-white" : "border-sky-300 bg-white text-sky-800 hover:border-sky-500"}`}
                  data-testid="dup-type-predisposizione"
                >
                  <ClipIcon className="h-3.5 w-3.5" /> Predisposizione
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, intervention_type: "prima" })}
                  className={`flex items-center gap-1.5 rounded-sm border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${form.intervention_type === "prima" ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-300 bg-white text-emerald-800 hover:border-emerald-500"}`}
                  data-testid="dup-type-prima"
                >
                  Prima installazione
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-auto rounded-sm border-2 border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:border-red-500 hover:bg-red-50"
                  data-testid="dup-cancel-btn"
                >
                  Annulla — è un errore
                </button>
              </div>
            </div>
          )}

          {dupCheck.loading && !hasDuplicates && (form.intervention_ref || "").trim() && (
            <div className="mt-2 text-xs text-slate-400" data-testid="dup-checking">Verifica duplicati…</div>
          )}

          {currentListino && (
            <div className="mt-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.voci_disponibili_clicca_per_aggiungere")}</Label>
                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={voceQuery}
                    onChange={(e) => setVoceQuery(e.target.value)}
                    placeholder="Cerca voce (es. sbr...)"
                    className="h-8 rounded-sm pl-7 text-xs"
                    data-testid="voce-search"
                  />
                  {voceQuery && (
                    <button
                      type="button"
                      onClick={() => setVoceQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 hover:text-slate-700"
                      data-testid="voce-search-clear"
                    >✕</button>
                  )}
                </div>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">
                {filteredVoci.length} / {(currentListino.voci || []).length} voci
              </div>
              <div className="mt-2 flex max-h-72 flex-wrap gap-2 overflow-y-auto">
                {filteredVoci.length === 0 ? (
                  <div className="w-full py-3 text-center text-xs text-slate-400" data-testid="voci-empty">
                    Nessuna voce corrisponde a "<b>{voceQuery}</b>"
                  </div>
                ) : filteredVoci.map((v, i) => (
                  <button key={`${v.code}-${i}`} onClick={() => addRiga(v)} data-testid={`add-voce-${v.code}`} className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs transition-colors duration-150 hover:border-[#0052FF] hover:bg-[#0052FF] hover:text-white">
                    <span className="font-mono font-medium">{v.code}</span> — {v.description} <span className="ml-1 text-slate-500">€{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 rounded-sm border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.righe")}</Label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addRigaCustom}
                  className="rounded-sm border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                  data-testid="add-riga-custom"
                  title="Aggiungi una voce fuori listino (una tantum)"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Voce libera (una tantum)
                </Button>
                <div className="font-mono text-lg font-semibold text-slate-900">Totale: € {total.toFixed(2)}</div>
              </div>
            </div>
            {form.righe.length === 0 && <div className="py-4 text-center text-xs text-slate-400">{t("auto.nessuna_voce_inserita")}</div>}
            {form.righe.map((r, i) => (
              <div key={i} className="mb-2 grid grid-cols-12 gap-2">
                <Input value={r.code} onChange={(e) => updRiga(i, "code", e.target.value)} className="col-span-2 rounded-sm font-mono" />
                <Input value={r.description} onChange={(e) => updRiga(i, "description", e.target.value)} className="col-span-5 rounded-sm" />
                <Input type="number" step="0.01" value={r.quantity} onChange={(e) => updRiga(i, "quantity", e.target.value)} className="col-span-1 rounded-sm font-mono" />
                <Input type="number" step="0.01" value={r.unit_price} onChange={(e) => updRiga(i, "unit_price", e.target.value)} className="col-span-2 rounded-sm font-mono" />
                <div className="col-span-1 flex items-center justify-center font-mono text-sm">€ {(r.quantity * r.unit_price).toFixed(2)}</div>
                <Button size="sm" variant="ghost" onClick={() => rmRiga(i)} className="col-span-1"><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}
          </div>
          </div>

          <DialogFooter className="flex-shrink-0 border-t border-slate-100 pt-3"><Button onClick={save} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="lav-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Adegua Listino */}
      <Dialog open={adeguaOpen} onOpenChange={setAdeguaOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Adegua listino
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <b>Come funziona:</b> aggiorna i prezzi unitari (<code>unit_price</code>) delle righe di lavorazione al valore attuale del listino, e ricalcola il totale. Utile quando un codice cambia prezzo a metà mese (es. monofibra interna €70 → €60) e vuoi riallineare le lavorazioni già inserite senza rifarle da capo.
              <div className="mt-1 opacity-80">Vengono saltate: lavorazioni con totale forzato manualmente (<i>total_override</i>) e righe "una tantum" senza listino.</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data da *</Label>
                <Input type="date" value={adeguaForm.date_from} onChange={(e) => setAdeguaForm({ ...adeguaForm, date_from: e.target.value })} className="rounded-sm" data-testid="adegua-date-from" />
              </div>
              <div>
                <Label className="text-xs">Data a *</Label>
                <Input type="date" value={adeguaForm.date_to} onChange={(e) => setAdeguaForm({ ...adeguaForm, date_to: e.target.value })} className="rounded-sm" data-testid="adegua-date-to" />
              </div>
              <div>
                <Label className="text-xs">Committente (opzionale)</Label>
                <Select value={adeguaForm.committente_id} onValueChange={(v) => setAdeguaForm({ ...adeguaForm, committente_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="adegua-committente"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i committenti</SelectItem>
                    {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tecnico (opzionale)</Label>
                <Select value={adeguaForm.technician_id} onValueChange={(v) => setAdeguaForm({ ...adeguaForm, technician_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="adegua-tecnico"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tecnici</SelectItem>
                    {technicians.map((tt) => <SelectItem key={tt.id} value={tt.id}>{tt.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {adeguaResult && (
              <div className="mt-2 space-y-2">
                <div className={`rounded-sm border p-3 text-sm ${adeguaResult.dry_run ? "border-sky-300 bg-sky-50" : "border-emerald-300 bg-emerald-50"}`}>
                  <div className="mb-1 font-semibold">
                    {adeguaResult.dry_run ? "Anteprima (nulla è stato salvato)" : "Adeguamento applicato ✓"}
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    <div>Lavorazioni analizzate: <b>{adeguaResult.lavorazioni_analyzed}</b></div>
                    <div>Lavorazioni da aggiornare: <b>{adeguaResult.lavorazioni_affected}</b></div>
                    <div>Righe da aggiornare: <b>{adeguaResult.rows_updated_total}</b></div>
                    <div>Skippate (totale forzato): <b>{adeguaResult.skipped_total_override}</b></div>
                    <div>Skippate (una tantum): <b>{adeguaResult.skipped_una_tantum}</b></div>
                    <div>Delta totale: <b className={adeguaResult.delta_sum >= 0 ? "text-emerald-700" : "text-red-700"}>{adeguaResult.delta_sum >= 0 ? "+" : ""}€ {adeguaResult.delta_sum.toFixed(2)}</b></div>
                  </div>
                </div>

                {adeguaResult.sample_first_20 && adeguaResult.sample_first_20.length > 0 && (
                  <div className="max-h-64 overflow-y-auto rounded-sm border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 border-b bg-slate-50 text-left font-bold uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="p-2">Data</th>
                          <th className="p-2">Impianto</th>
                          <th className="p-2 text-right">Vecchio tot.</th>
                          <th className="p-2 text-right">Nuovo tot.</th>
                          <th className="p-2 text-right">Δ</th>
                          <th className="p-2 text-right">Righe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adeguaResult.sample_first_20.map((s) => (
                          <tr key={s.id} className="border-b border-slate-100">
                            <td className="p-2 font-mono">{s.date}</td>
                            <td className="p-2">{s.intervention_ref || "—"}</td>
                            <td className="p-2 text-right">€ {s.old_total.toFixed(2)}</td>
                            <td className="p-2 text-right font-semibold">€ {s.new_total.toFixed(2)}</td>
                            <td className={`p-2 text-right ${s.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>{s.delta >= 0 ? "+" : ""}€ {s.delta.toFixed(2)}</td>
                            <td className="p-2 text-right">{s.rows_updated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAdeguaOpen(false)} className="rounded-sm">Annulla</Button>
            <Button
              variant="outline"
              onClick={() => runAdegua(true)}
              disabled={adeguaLoading}
              className="rounded-sm border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="adegua-preview"
            >
              {adeguaLoading ? "Analisi..." : "Anteprima (dry-run)"}
            </Button>
            <Button
              onClick={() => {
                if (!adeguaResult || adeguaResult.dry_run === false) return runAdegua(false);
                const ok = window.confirm(
                  `Confermare l'adeguamento?\n\n${adeguaResult.lavorazioni_affected} lavorazioni verranno aggiornate.\nDelta totale: € ${adeguaResult.delta_sum.toFixed(2)}\n\nL'azione non può essere annullata.`
                );
                if (ok) runAdegua(false);
              }}
              disabled={adeguaLoading || !adeguaResult || adeguaResult.lavorazioni_affected === 0}
              className="rounded-sm bg-amber-600 hover:bg-amber-700"
              data-testid="adegua-apply"
            >
              {adeguaLoading ? "Adeguamento..." : `Applica adeguamento${adeguaResult ? ` (${adeguaResult.lavorazioni_affected})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
